import React, { useEffect, useRef, useState, FC } from 'react';
import { Flex } from 'rebass/styled-components';
import FileDropZone from 'components/fileDropzone';
import useProject from 'hooks/useProject';
import useProjects from 'hooks/useProjects';
import { Spinner } from 'components/spinner';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import useFeature from 'hooks/useFeature';
import useDefaultFieldsListener from 'hooks/useDefaultFieldsListener';
import { SUGGEST } from 'components/FieldSuggestions';
import { ui, statusColor, stripButton, button } from 'util/ui';
import { TickIcon, CrossIcon, DeleteIcon, DuplicateIcon, ImageIcon } from 'components/shell/Icons';
import useMainTags from 'hooks/useMainTags';
import { isShown, showKeyFor, CUSTOM_FIELDS } from 'views/admin/types';

/**
 * Project details, rebuilt to the "Archy UI Refresh" design (Nov 2026):
 * - status shown by the frame colour and a tick/cross icon (no words), quick actions on the strip;
 * - a 16:9 still image on the left with "Change image" (click or drop, as before);
 * - a large title and a grid of fields that edit in place, then tag pills.
 * Data handling is unchanged: each field saves when you leave it.
 */

type Props = {
  project: IProject;
};

const editStyle = (size = 14, weight = 500): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  font: 'inherit',
  fontSize: size,
  fontWeight: weight,
  color: ui.ink,
  border: '1px solid transparent',
  borderRadius: 8,
  padding: '4px 7px',
  marginLeft: -8,
  background: 'transparent',
  minWidth: 0,
});

/** Every field value the same height, including the date picker, so a row lines up (Aaron, 5 Nov 2026). */
const FIELD_HEIGHT = 30;
/** Every field heading the same height, whether plain text or an editable custom-field name. */
const LABEL_HEIGHT = 20;

const labelStyle: React.CSSProperties = {
  font: 'inherit',
  fontSize: 12,
  lineHeight: '16px',
  color: ui.muted,
  border: '1px solid transparent',
  borderRadius: 6,
  padding: '1px 7px',
  marginLeft: -8,
  background: 'transparent',
  width: '100%',
  height: LABEL_HEIGHT,
  boxSizing: 'border-box',
};

/** A plain field heading, the same box as the editable custom-field names. */
const dtStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  color: ui.muted,
  height: LABEL_HEIGHT,
  padding: '2px 0',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** Budget is always shown in dollars: "$12,500". Stored as a plain number. */
const budgetDigits = (v: unknown) => String(v == null ? '' : v).replace(/[^0-9.]/g, '');
const showBudget = (digits: string) => {
  if (!digits) return '';
  const n = Number(digits);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-AU', { maximumFractionDigits: 2 })}` : `$${digits}`;
};

/** One size for tags, suggested tags and the "+ Tag" field. */
const tagBox: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 28,
  boxSizing: 'border-box',
  padding: '0 10px',
  borderRadius: 6,
  font: 'inherit',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
};

const ProjectInfo: FC<Props> = ({ project }) => {
  const dispatch = useDispatch();
  const { updateProject, removeProject } = useProject(project.id);
  const { duplicateProject } = useProjects();

  const [title, setTitle] = useState(project.title);
  const [jobNumber, setJobNumber] = useState(project.jobNumber);
  const [client, setClient] = useState(project.client);
  const [budget, setBudget] = useState(budgetDigits(project.budget));
  const [budgetFocus, setBudgetFocus] = useState(false);
  const [tags, setTags] = useState<string[]>(Array.isArray(project.tags) ? project.tags : []);
  const [agency, setAgency] = useState(project.agency);
  const [newTag, setNewTag] = useState('');
  const [custom, setCustom] = useState<ICustomField[]>([
    project.customField1 || { key: 'Director', value: '' },
    project.customField2 || { key: 'Producer', value: '' },
    project.customField3 || { key: 'DOP', value: '' },
  ]);
  const { uploadFeature } = useFeature(project.id);
  const { defaults } = useDefaultFieldsListener();
  const mainTags = useMainTags();
  const [imageLoading, setImageLoading] = useState(false);
  const [imageHover, setImageHover] = useState(false);

  // Save tags only when someone changes them. (Before Nov 2026 this also ran on opening a project,
  // which re-saved the project, and so wrote an activity-log entry, every time anyone opened it.)
  const firstTags = useRef(true);
  useEffect(() => {
    if (firstTags.current) {
      firstTags.current = false;
      return;
    }
    updateProject(project.id, { tags });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (tag && !tags.includes(tag)) setTags((current) => [...current, tag]);
    setNewTag('');
  };
  const removeTag = (tag: string) => setTags((current) => current.filter((t) => t !== tag));

  const save = (data: Partial<IProject>) => updateProject(project.id, data);
  const saveCustom = (index: number) =>
    save({ [`customField${index + 1}`]: custom[index] } as Partial<IProject>);

  const upload = async (file: File) => {
    uploadFeature(file, (task) => {
      task.on(
        'state_changed',
        () => setImageLoading(true),
        () => setImageLoading(false),
        () => setImageLoading(false),
      );
    });
  };

  const color = statusColor(!!project.complete);
  const toggleLabel = project.complete ? 'Complete. Click to mark incomplete' : 'Incomplete. Click to mark complete';
  const dateValue = project.date ? moment.unix(Number(project.date)).format('YYYY-MM-DD') : '';
  const imageBoxId = `feature-${project.id}`;

  // Labels come from Admin › Settings › Project fields; blank there means the standard name.
  const label = (own: string | undefined, standard: string) => (own && own.trim()) || standard;
  const fields: { label: string; value: string; set(v: string): void; key: keyof IProject; list?: string }[] = [
    { label: label(defaults?.projectNumber, 'Project number'), value: jobNumber || '', set: setJobNumber, key: 'jobNumber', list: SUGGEST.jobNumber },
    { label: label(defaults?.client, 'Client'), value: client || '', set: setClient, key: 'client', list: SUGGEST.client },
    { label: label(defaults?.agency, 'Agency'), value: agency || '', set: setAgency, key: 'agency', list: SUGGEST.agency },
  ];
  const budgetLabel = 'Budget';
  const dateLabel = 'Date';
  // Admin › Settings › Project fields can hide Budget and each custom field; what's filled in is kept.
  const budgetVisible = isShown(defaults, 'showBudget');

  return (
    <section
      aria-label="Project details"
      style={{ background: ui.surface, border: `2px solid ${color}`, borderRadius: 16, overflow: 'hidden', fontFamily: ui.font, color: ui.ink }}
    >
      {/* Status strip: colour + icon only; quick actions */}
      <div style={{ background: color, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '6px 8px' }}>
        <button
          type="button"
          className="archy-iconbtn"
          onClick={() => dispatch(push('/'))}
          style={{ ...stripButton, width: 'auto', padding: '0 12px', color: '#FFFFFF', font: 'inherit', fontSize: 13, fontWeight: 600 }}
        >
          ← Projects
        </button>
        <span style={{ flex: 1 }} />
        {/* Status, duplicate and delete together on the right (Aaron, 5 Nov 2026) */}
        <button
          type="button"
          className="archy-iconbtn"
          aria-label={toggleLabel}
          title={toggleLabel}
          style={stripButton}
          onClick={() => save({ complete: !project.complete })}
        >
          {project.complete ? <TickIcon /> : <CrossIcon />}
        </button>
        <button
          type="button"
          className="archy-iconbtn"
          aria-label="Duplicate project"
          title="Duplicate"
          style={stripButton}
          onClick={() => window.confirm('Are you sure you want to duplicate this project?') && duplicateProject(project)}
        >
          <DuplicateIcon />
        </button>
        <button
          type="button"
          className="archy-iconbtn"
          aria-label="Delete project"
          title="Delete project"
          style={stripButton}
          onClick={() => removeProject(project.id)}
        >
          <DeleteIcon />
        </button>
      </div>

      <div style={{ padding: 20, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {/* 16:9 still image, chosen inside the project as before (click or drop an image) */}
        {/* A fixed 16:9 frame showing the whole image, never cropped (Aaron, 5 Nov 2026). The column is
            wide enough that the frame sits close to the height of the details beside it. */}
        <div style={{ flex: '0 1 500px', minWidth: 260, alignSelf: 'flex-start' }}>
          <div
            id={imageBoxId}
            title="Change image (click, or drop an image here)"
            onMouseEnter={() => setImageHover(true)}
            onMouseLeave={() => setImageHover(false)}
            style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', background: '#16181D' }}
          >
            {/* Hover overlay instead of a separate button: click the image to change it */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 3,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(22,24,29,0.45)',
                color: '#FFFFFF',
                fontSize: 14,
                fontWeight: 600,
                gap: 8,
                opacity: imageHover && !imageLoading ? 1 : 0,
                transition: 'opacity 0.15s ease',
              }}
            >
              <ImageIcon size={20} color="#FFFFFF" />
              Change image
            </div>
            <FileDropZone upload={upload} allowClick={true}>
              {imageLoading && (
                <Flex sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 2 }} justifyContent="center" alignItems="center">
                  <Spinner />
                </Flex>
              )}
              {project.featureURL ? (
                <div
                  role="img"
                  aria-label="Project image"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: imageLoading ? 0.25 : 1,
                    backgroundImage: `url('${project.featureURL}')`,
                    // contain: the whole image shows; a 16:9 still fills the frame exactly.
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    cursor: 'pointer',
                  }}
                />
              ) : (
                <Flex sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, cursor: 'pointer' }} justifyContent="center" alignItems="center">
                  <ImageIcon size={32} />
                </Flex>
              )}
            </FileDropZone>
          </div>
        </div>

        {/* Title, fields, tags */}
        {/* Stretches to the image's height: the title and fields get room, and the tags sit along the
            bottom, level with the bottom of the image (Aaron, 5 Nov 2026). */}
        <div style={{ flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input
            className="archy-edit"
            aria-label={defaults?.title ?? 'Title'}
            // Follows the title label from Admin › Settings when it has been renamed (Admin request #4).
            placeholder={
              defaults?.title && defaults.title.trim() && defaults.title.trim().toLowerCase() !== 'title'
                ? `Add ${defaults.title.trim().toLowerCase()}`
                : 'Add project title'
            }
            value={title || ''}
            list={SUGGEST.title}
            autoComplete="off"
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => save({ title: e.target.value })}
            style={{ ...editStyle(26, 700), letterSpacing: '-0.02em' }}
          />

          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '14px 20px' }}>
            {fields.map((f) => (
              <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <dt style={dtStyle}>{f.label}</dt>
                <dd style={{ margin: 0 }}>
                  <input
                    className="archy-edit"
                    aria-label={f.label}
                    value={f.value}
                    list={f.list}
                    autoComplete="off"
                    placeholder={`Add ${f.label.toLowerCase()}`}
                    onChange={(e) => f.set(e.target.value)}
                    onBlur={(e) => save({ [f.key]: e.target.value } as Partial<IProject>)}
                    style={{ ...editStyle(), height: FIELD_HEIGHT }}
                  />
                </dd>
              </div>
            ))}
            {/* Budget: always in dollars. Shows "$12,500"; while typing, just the number. */}
            {budgetVisible && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <dt style={dtStyle}>{budgetLabel}</dt>
              <dd style={{ margin: 0 }}>
                <input
                  className="archy-edit"
                  aria-label={`${budgetLabel} in dollars`}
                  inputMode="decimal"
                  value={budgetFocus ? budget : showBudget(budget)}
                  placeholder="$0"
                  autoComplete="off"
                  onFocus={() => setBudgetFocus(true)}
                  onChange={(e) => setBudget(budgetDigits(e.target.value))}
                  onBlur={() => {
                    setBudgetFocus(false);
                    const n = budget ? Number(budget) : 0;
                    if (Number.isFinite(n) && n !== Number(budgetDigits(project.budget) || 0)) save({ budget: n });
                  }}
                  style={{ ...editStyle(), height: FIELD_HEIGHT }}
                />
              </dd>
            </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <dt style={dtStyle}>{dateLabel}</dt>
              <dd style={{ margin: 0 }}>
                <input
                  className="archy-edit"
                  aria-label={dateLabel}
                  type="date"
                  value={dateValue}
                  onChange={(e) => e.target.value && save({ date: moment(e.target.value, 'YYYY-MM-DD').unix() })}
                  style={{ ...editStyle(), height: FIELD_HEIGHT }}
                />
              </dd>
            </div>
            {custom.map((f, i) => !isShown(defaults, showKeyFor(CUSTOM_FIELDS[i])) ? null : (
              <div key={`custom${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* flex: no extra line space under the name box, so this heading is exactly as tall as the others */}
                <dt style={{ height: LABEL_HEIGHT, display: 'flex' }}>
                  <input
                    className="archy-edit"
                    aria-label={`Name of custom field ${i + 1}`}
                    value={f.key}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustom((c) => c.map((x, j) => (j === i ? { ...x, key: v } : x)));
                    }}
                    onBlur={() => saveCustom(i)}
                    style={labelStyle}
                  />
                </dt>
                <dd style={{ margin: 0 }}>
                  <input
                    className="archy-edit"
                    aria-label={f.key}
                    value={f.value}
                    list={SUGGEST.people}
                    autoComplete="off"
                    placeholder={`Add ${f.key}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustom((c) => c.map((x, j) => (j === i ? { ...x, value: v } : x)));
                    }}
                    onBlur={() => saveCustom(i)}
                    style={{ ...editStyle(), height: FIELD_HEIGHT }}
                  />
                </dd>
              </div>
            ))}
          </dl>

          {/* Tags, pushed to the bottom of the column */}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            {tags.map((t) => (
              <span key={t} style={{ ...tagBox, background: ui.subtle, border: `1px solid ${ui.subtle}`, color: ui.text2, paddingRight: 4 }}>
                {t}
                <button
                  type="button"
                  aria-label={`Remove tag ${t}`}
                  title="Remove tag"
                  onClick={() => removeTag(t)}
                  style={{ border: 'none', background: 'transparent', padding: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: 4 }}
                >
                  <CrossIcon size={11} color={ui.muted} width={2.6} />
                </button>
              </span>
            ))}
            <input
              aria-label="Add a tag"
              placeholder="+ Tag"
              value={newTag}
              list={SUGGEST.tags}
              autoComplete="off"
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag(newTag);
                }
              }}
              onBlur={() => newTag.trim() && addTag(newTag)}
              style={{ ...tagBox, color: ui.ink, background: 'transparent', border: `1px dashed ${ui.control}`, width: 90, outline: 'none' }}
            />
          </div>
          {/* Suggested main tags: same box and text size as the tags above, outlined (Aaron, 5 Nov 2026). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {mainTags.filter((t) => !tags.includes(t)).map((t) => (
              <button
                key={t}
                type="button"
                className="archy-btn"
                title={`Add tag "${t}"`}
                onClick={() => addTag(t)}
                style={{ ...tagBox, color: ui.muted, background: ui.surface, border: `1px solid ${ui.control}`, cursor: 'pointer' }}
              >
                + {t}
              </button>
            ))}
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectInfo;
